import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "../App.css";
import "./HeroSection.css";

import { Viewer, Worker } from "@react-pdf-viewer/core";

import "@react-pdf-viewer/core/lib/styles/index.css";

// Hero title typed as one continuous stream across two visual lines.
const TITLE_LINE_1 = "Van Vlimmeren";
const TITLE_LINE_2 = "Feestverhuur";
const TITLE_LENGTH = TITLE_LINE_1.length + TITLE_LINE_2.length;
const TYPE_SPEED = 70; // ms per character — deliberately slow
const TYPE_START_DELAY = 350; // let the eyebrow settle before typing

function HeroSection() {
	const [showPDF, setShowPDF] = useState(false);
	const [zoomLevel, setZoomLevel] = useState(0.5);

	// Typewriter: a single character count drives both lines so the reveal
	// reads as one continuous line even though it wraps to two.
	const [typedCount, setTypedCount] = useState(0);
	const [typingDone, setTypingDone] = useState(false);

	const photoRef = useRef(null);

	useEffect(() => {
		const prefersReduced = window.matchMedia(
			"(prefers-reduced-motion: reduce)"
		).matches;
		if (prefersReduced) return;

		let raf = 0;
		const onScroll = () => {
			if (raf) return;
			raf = requestAnimationFrame(() => {
				const y = window.scrollY;
				if (photoRef.current && y < window.innerHeight) {
					photoRef.current.style.transform = `translate3d(0, ${y * 0.18}px, 0)`;
				}
				raf = 0;
			});
		};
		window.addEventListener("scroll", onScroll, { passive: true });
		return () => {
			window.removeEventListener("scroll", onScroll);
			if (raf) cancelAnimationFrame(raf);
		};
	}, []);

	useEffect(() => {
		const prefersReduced = window.matchMedia(
			"(prefers-reduced-motion: reduce)"
		).matches;

		if (prefersReduced) {
			setTypedCount(TITLE_LENGTH);
			setTypingDone(true);
			return;
		}

		let count = 0;
		let intervalId;
		const startId = setTimeout(() => {
			intervalId = setInterval(() => {
				count += 1;
				setTypedCount(count);
				if (count >= TITLE_LENGTH) {
					clearInterval(intervalId);
					setTypingDone(true);
				}
			}, TYPE_SPEED);
		}, TYPE_START_DELAY);

		return () => {
			clearTimeout(startId);
			clearInterval(intervalId);
		};
	}, []);

	// Fade the caret out a beat after the title finishes.
	const [caretVisible, setCaretVisible] = useState(true);
	useEffect(() => {
		if (!typingDone) return;
		const hideId = setTimeout(() => setCaretVisible(false), 1600);
		return () => clearTimeout(hideId);
	}, [typingDone]);

	const line1Shown = Math.min(typedCount, TITLE_LINE_1.length);
	const line2Shown = Math.max(0, typedCount - TITLE_LINE_1.length);
	// Caret stays on line 1 until it is full, then moves to line 2.
	const caretOnLine1 = typedCount < TITLE_LINE_1.length;

	const handleZoomIn = () => {
		setZoomLevel((prevZoom) => Math.min(prevZoom + 0.25, 3.0));
	};

	const handleZoomOut = () => {
		setZoomLevel((prevZoom) => Math.max(prevZoom - 0.25, 0.5));
	};

	return (
		<section className="hero-container">
			<div className="hero-photo" aria-hidden="true" ref={photoRef}>
				<div className="hero-photo-inner"></div>
			</div>
			<div className="hero-vignette" aria-hidden="true"></div>
			<div className="hero-grain" aria-hidden="true"></div>

			<div className="hero-content">
				<span className="hero-eyebrow">EXCLUSIEVE FEESTDECORATIE</span>
				<h1
					className="hero-display"
					aria-label={`${TITLE_LINE_1} ${TITLE_LINE_2}`}>
					<span className="hero-display-line" aria-hidden="true">
						<span className="hero-typed">
							{TITLE_LINE_1.slice(0, line1Shown)}
						</span>
						{caretOnLine1 && caretVisible && (
							<span className="hero-caret" />
						)}
						<span className="hero-untyped">
							{TITLE_LINE_1.slice(line1Shown)}
						</span>
					</span>
					<span className="hero-display-line" aria-hidden="true">
						<span className="hero-typed">
							{TITLE_LINE_2.slice(0, line2Shown)}
						</span>
						{!caretOnLine1 && caretVisible && (
							<span
								className={`hero-caret${
									typingDone ? " hero-caret--idle" : ""
								}`}
							/>
						)}
						<span className="hero-untyped">
							{TITLE_LINE_2.slice(line2Shown)}
						</span>
					</span>
				</h1>
				<div className="hero-rule" aria-hidden="true"></div>
				<p className="hero-subline">
					&ldquo;Uw feest, onze zorg&rdquo;
				</p>
				<div className="hero-ctas">
					<Link
						to="/producten"
						className="btn btn--primary btn--on-dark btn--lg">
						Ontdek de collectie
					</Link>
					<button
						type="button"
						className="btn btn--secondary btn--on-dark btn--lg"
						onClick={() => setShowPDF(true)}>
						Catalogus 2026
					</button>
				</div>
			</div>

			{showPDF && (
				<div className="pdf-overlay">
					<div className="zoom-controls">
						<span>{Math.round(zoomLevel * 100)}%</span>
						<button onClick={handleZoomOut}>Zoom uit</button>
						<button onClick={handleZoomIn}>Zoom in</button>
						<button
							className="close-pdf-button"
							onClick={() => setShowPDF(false)}>
							Sluiten
						</button>
					</div>

					<Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
						<div className="pdf-fullscreen">
							<Viewer
								key={zoomLevel}
								fileUrl="/images/Catalogus VVF-gecomprimeerd.pdf"
								defaultScale={zoomLevel}
								theme="dark"
							/>
						</div>
					</Worker>
				</div>
			)}
		</section>
	);
}

export default HeroSection;
